/**
 * 五子棋游戏主应用
 * 集成游戏逻辑、AI和用户界面
 */
import { Board } from './board.js';
import { GomokuAI } from './ai.js';

class GomokuGame {
    constructor() {
        this.board = new Board();
        this.ai = new GomokuAI();
        this.canvas = null;
        this.ctx = null;
        this.cellSize = 40;
        this.padding = 20;
        this.boardSize = 15;
        this.humanPlayer = 1; // 人类玩家默认黑棋
        this.aiPlayer = 2;    // AI默认白棋
        this.isAiThinking = false;

        this.initializeElements();
        this.setupEventListeners();
        this.initializeCanvas();
        this.drawBoard();
        this.updateUI();
    }

    /**
     * 初始化DOM元素
     */
    initializeElements() {
        this.elements = {
            canvas: document.getElementById('gameBoard'),
            currentPlayerIndicator: document.getElementById('currentPlayerIndicator'),
            gameStatus: document.getElementById('gameStatus'),
            difficulty: document.getElementById('difficulty'),
            firstPlayer: document.getElementById('firstPlayer'),
            newGameBtn: document.getElementById('newGame'),
            undoBtn: document.getElementById('undo'),
            resignBtn: document.getElementById('resign'),
            moveHistory: document.getElementById('moveHistory'),
            loadingOverlay: document.getElementById('loadingOverlay'),
            gameOverModal: document.getElementById('gameOverModal'),
            gameOverTitle: document.getElementById('gameOverTitle'),
            gameOverMessage: document.getElementById('gameOverMessage'),
            playAgainBtn: document.getElementById('playAgain'),
            closeModalBtn: document.getElementById('closeModal'),
            // AI统计
            aiStats: document.getElementById('aiStats'),
            nodesSearched: document.getElementById('nodesSearched'),
            thinkTime: document.getElementById('thinkTime'),
            nodesPerSecond: document.getElementById('nodesPerSecond')
        };
    }

    /**
     * 设置事件监听器
     */
    setupEventListeners() {
        // 画布点击事件
        this.elements.canvas.addEventListener('click', this.handleCanvasClick.bind(this));

        // 控制按钮
        this.elements.newGameBtn.addEventListener('click', this.startNewGame.bind(this));
        this.elements.undoBtn.addEventListener('click', this.undoMove.bind(this));
        this.elements.resignBtn.addEventListener('click', this.resign.bind(this));

        // 游戏设置
        this.elements.difficulty.addEventListener('change', this.onDifficultyChange.bind(this));
        this.elements.firstPlayer.addEventListener('change', this.onFirstPlayerChange.bind(this));

        // 模态框
        this.elements.playAgainBtn.addEventListener('click', () => {
            this.hideGameOverModal();
            this.startNewGame();
        });

        this.elements.closeModalBtn.addEventListener('click', this.hideGameOverModal.bind(this));

        // 窗口大小改变事件
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    /**
     * 初始化画布
     */
    initializeCanvas() {
        this.canvas = this.elements.canvas;
        this.ctx = this.canvas.getContext('2d');

        // 设置画布实际尺寸
        const canvasSize = this.cellSize * (this.boardSize - 1) + this.padding * 2;
        this.canvas.width = canvasSize;
        this.canvas.height = canvasSize;

        // 设置高清显示
        const dpr = window.devicePixelRatio || 1;
        this.canvas.style.width = canvasSize + 'px';
        this.canvas.style.height = canvasSize + 'px';
        this.canvas.width = canvasSize * dpr;
        this.canvas.height = canvasSize * dpr;
        this.ctx.scale(dpr, dpr);

        // 存储实际显示尺寸，用于点击坐标计算
        this.displaySize = canvasSize;
        this.devicePixelRatio = dpr;

        // 初始化画布显示尺寸
        this.handleResize();
    }

    /**
     * 绘制棋盘
     */
    drawBoard() {
        const ctx = this.ctx;
        const canvasSize = this.cellSize * (this.boardSize - 1) + this.padding * 2;

        // 清空画布
        ctx.clearRect(0, 0, canvasSize, canvasSize);

        // 绘制棋盘背景
        ctx.fillStyle = '#f4d03f';
        ctx.fillRect(0, 0, canvasSize, canvasSize);

        // 绘制网格线
        ctx.strokeStyle = '#8b4513';
        ctx.lineWidth = 1;

        for (let i = 0; i < this.boardSize; i++) {
            // 垂直线
            ctx.beginPath();
            ctx.moveTo(this.padding + i * this.cellSize, this.padding);
            ctx.lineTo(this.padding + i * this.cellSize, canvasSize - this.padding);
            ctx.stroke();

            // 水平线
            ctx.beginPath();
            ctx.moveTo(this.padding, this.padding + i * this.cellSize);
            ctx.lineTo(canvasSize - this.padding, this.padding + i * this.cellSize);
            ctx.stroke();
        }

        // 绘制星位（天元和四个角的星位）
        const starPoints = [
            [7, 7], // 天元
            [3, 3], [11, 3], [3, 11], [11, 11] // 四个角的星位
        ];

        ctx.fillStyle = '#8b4513';
        starPoints.forEach(([row, col]) => {
            ctx.beginPath();
            ctx.arc(
                this.padding + col * this.cellSize,
                this.padding + row * this.cellSize,
                3,
                0,
                2 * Math.PI
            );
            ctx.fill();
        });

        // 绘制所有棋子
        for (let row = 0; row < this.boardSize; row++) {
            for (let col = 0; col < this.boardSize; col++) {
                if (this.board.board[row][col] !== 0) {
                    this.drawPiece(row, col, this.board.board[row][col]);
                }
            }
        }

        // 标记最后一步
        if (this.board.lastMove) {
            this.markLastMove(this.board.lastMove.row, this.board.lastMove.col);
        }
    }

    /**
     * 绘制棋子
     */
    drawPiece(row, col, player) {
        const ctx = this.ctx;
        const x = this.padding + col * this.cellSize;
        const y = this.padding + row * this.cellSize;
        const radius = this.cellSize * 0.4;

        // 绘制阴影
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);

        if (player === 1) {
            // 黑棋
            const gradient = ctx.createRadialGradient(x - radius/3, y - radius/3, 0, x, y, radius);
            gradient.addColorStop(0, '#555');
            gradient.addColorStop(1, '#000');
            ctx.fillStyle = gradient;
        } else {
            // 白棋
            const gradient = ctx.createRadialGradient(x - radius/3, y - radius/3, 0, x, y, radius);
            gradient.addColorStop(0, '#fff');
            gradient.addColorStop(1, '#ddd');
            ctx.fillStyle = gradient;
        }

        ctx.fill();

        // 重置阴影
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // 绘制边框
        ctx.strokeStyle = player === 1 ? '#000' : '#999';
        ctx.lineWidth = 1;
        ctx.stroke();
    }

    /**
     * 标记最后一步
     */
    markLastMove(row, col) {
        const ctx = this.ctx;
        const x = this.padding + col * this.cellSize;
        const y = this.padding + row * this.cellSize;

        ctx.strokeStyle = '#ff4444';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, this.cellSize * 0.2, 0, 2 * Math.PI);
        ctx.stroke();
    }

    /**
     * 处理画布点击
     */
    handleCanvasClick(event) {
        if (this.board.gameOver || this.isAiThinking) {
            return;
        }

        if (this.board.currentPlayer !== this.humanPlayer) {
            return; // 不是人类玩家回合
        }

        const rect = this.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // 计算缩放比例
        const scaleX = this.displaySize / this.canvasDisplayWidth;
        const scaleY = this.displaySize / this.canvasDisplayHeight;

        // 将点击坐标转换为画布坐标
        const canvasX = x * scaleX;
        const canvasY = y * scaleY;

        // 转换为棋盘坐标
        const col = Math.round((canvasX - this.padding) / this.cellSize);
        const row = Math.round((canvasY - this.padding) / this.cellSize);

        // 验证坐标有效性
        if (row >= 0 && row < this.boardSize && col >= 0 && col < this.boardSize) {
            // 尝试落子
            if (this.board.makeMove(row, col)) {
                this.drawBoard();
                this.updateUI();

                // 检查游戏是否结束
                if (this.board.gameOver) {
                    this.handleGameOver();
                } else {
                    // AI回合
                    setTimeout(() => this.makeAiMove(), 500);
                }
            }
        }
    }

    /**
     * AI落子
     */
    async makeAiMove() {
        if (this.board.gameOver || this.board.currentPlayer !== this.aiPlayer) {
            return;
        }

        this.isAiThinking = true;
        this.showLoading();

        // 模拟异步AI思考
        const move = await new Promise(resolve => {
            setTimeout(() => {
                const bestMove = this.ai.getBestMove(this.board);
                resolve(bestMove);
            }, 100);
        });

        this.isAiThinking = false;
        this.hideLoading();

        if (move) {
            this.board.makeMove(move.row, move.col);
            this.drawBoard();
            this.updateUI();
            this.updateAiStats();

            if (this.board.gameOver) {
                this.handleGameOver();
            }
        }
    }

    /**
     * 更新AI统计信息
     */
    updateAiStats() {
        const stats = this.ai.getStats();
        this.elements.nodesSearched.textContent = stats.nodesSearched.toLocaleString();
        this.elements.thinkTime.textContent = stats.timeElapsed + 'ms';
        this.elements.nodesPerSecond.textContent = stats.nodesPerSecond.toLocaleString();
    }

    /**
     * 开始新游戏
     */
    startNewGame() {
        this.board.reset();
        this.humanPlayer = this.elements.firstPlayer.value === 'human' ? 1 : 2;
        this.aiPlayer = 3 - this.humanPlayer;

        this.drawBoard();
        this.updateUI();
        this.elements.moveHistory.innerHTML = '<p>暂无走棋记录</p>';

        // 如果AI先手
        if (this.board.currentPlayer === this.aiPlayer) {
            setTimeout(() => this.makeAiMove(), 1000);
        }
    }

    /**
     * 悔棋
     */
    undoMove() {
        if (this.isAiThinking || this.board.gameOver) {
            return;
        }

        // 撤销两步（人类和AI各一步）
        const movesToUndo = this.board.moveHistory.length >= 2 ? 2 : 1;

        for (let i = 0; i < movesToUndo; i++) {
            this.board.undoMove();
        }

        this.drawBoard();
        this.updateUI();
    }

    /**
     * 认输
     */
    resign() {
        if (this.board.gameOver || this.isAiThinking) {
            return;
        }

        this.board.gameOver = true;
        this.board.winner = this.aiPlayer;
        this.handleGameOver();
    }

    /**
     * 更新UI
     */
    updateUI() {
        // 更新当前玩家指示
        const currentPlayerText = this.board.currentPlayer === 1 ? '⚫ 黑棋' : '⚪ 白棋';
        this.elements.currentPlayerIndicator.textContent = currentPlayerText;
        this.elements.currentPlayerIndicator.className =
            `player-indicator ${this.board.currentPlayer === 1 ? 'black' : 'white'}`;

        // 更新游戏状态
        if (this.board.gameOver) {
            if (this.board.winner === 0) {
                this.elements.gameStatus.textContent = '平局';
            } else if (this.board.winner === this.humanPlayer) {
                this.elements.gameStatus.textContent = '你赢了！';
            } else {
                this.elements.gameStatus.textContent = 'AI获胜';
            }
            this.elements.gameStatus.style.background = '#f8d7da';
            this.elements.gameStatus.style.color = '#721c24';
        } else {
            this.elements.gameStatus.textContent = '游戏进行中';
            this.elements.gameStatus.style.background = '#e8f5e8';
            this.elements.gameStatus.style.color = '#2d5a2d';
        }

        // 更新走棋历史
        this.updateMoveHistory();

        // 显示/隐藏AI统计
        this.elements.aiStats.style.display =
            this.board.currentPlayer === this.aiPlayer ? 'block' : 'none';
    }

    /**
     * 更新走棋历史
     */
    updateMoveHistory() {
        if (this.board.moveHistory.length === 0) {
            this.elements.moveHistory.innerHTML = '<p>暂无走棋记录</p>';
            return;
        }

        let historyHTML = '';
        this.board.moveHistory.forEach((move, index) => {
            const player = move.player === 1 ? '黑' : '白';
            const position = `${String.fromCharCode(65 + move.col)}${move.row + 1}`;
            const playerClass = move.player === 1 ? 'black-move' : 'white-move';
            historyHTML += `<span class="move-item ${playerClass}">${index + 1}. ${player}: ${position}</span>`;
        });

        this.elements.moveHistory.innerHTML = historyHTML;
    }

    /**
     * 处理游戏结束
     */
    handleGameOver() {
        let title, message;

        if (this.board.winner === 0) {
            title = '游戏结束';
            message = '这是一场平局！';
        } else if (this.board.winner === this.humanPlayer) {
            title = '恭喜你赢了！';
            message = '你成功战胜了AI！';
        } else {
            title = '很遗憾';
            message = 'AI获得了胜利，再接再厉！';
        }

        this.showGameOverModal(title, message);
    }

    /**
     * 显示游戏结束模态框
     */
    showGameOverModal(title, message) {
        this.elements.gameOverTitle.textContent = title;
        this.elements.gameOverMessage.textContent = message;
        this.elements.gameOverModal.style.display = 'flex';
    }

    /**
     * 隐藏游戏结束模态框
     */
    hideGameOverModal() {
        this.elements.gameOverModal.style.display = 'none';
    }

    /**
     * 显示加载遮罩
     */
    showLoading() {
        this.elements.loadingOverlay.style.display = 'flex';
    }

    /**
     * 隐藏加载遮罩
     */
    hideLoading() {
        this.elements.loadingOverlay.style.display = 'none';
    }

    /**
     * 处理难度改变
     */
    onDifficultyChange(event) {
        const difficulty = parseInt(event.target.value);
        this.ai.setDifficulty(difficulty);
    }

    /**
     * 处理先手方改变
     */
    onFirstPlayerChange(event) {
        const humanFirst = event.target.value === 'human';
        this.humanPlayer = humanFirst ? 1 : 2;
        this.aiPlayer = 3 - this.humanPlayer;
        this.startNewGame();
    }

    /**
     * 处理窗口大小改变
     */
    handleResize() {
        // 重新获取画布的实际显示尺寸
        const rect = this.canvas.getBoundingClientRect();
        this.canvasDisplayWidth = rect.width;
        this.canvasDisplayHeight = rect.height;
    }
}

// 初始化游戏
document.addEventListener('DOMContentLoaded', () => {
    new GomokuGame();
});